"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@rallly/ui/dialog";
import { Icon } from "@rallly/ui/icon";
import {
  CalendarCheckIcon,
  ClockIcon,
  CopyIcon,
  LifeBuoyIcon,
  SettingsIcon,
  UserPlusIcon,
} from "lucide-react";
import React from "react";
import { Trans } from "@/components/trans";
import { UpgradeButton } from "@/components/upgrade-button";
import { PLAN_NAMES } from "../constants";

function KeyBenefits({ children }: { children?: React.ReactNode }) {
  return <ul className="space-y-3">{children}</ul>;
}

function KeyBenefitsItem({
  icon,
  title,
  description,
}: {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-3">
      <div className="rounded-lg border bg-gradient-to-b from-white to-gray-50 p-2">
        <Icon>{icon}</Icon>
      </div>
      <div>
        <div className="mt-2 font-medium text-sm">{title}</div>
        <div className="mt-1 text-muted-foreground text-xs">{description}</div>
      </div>
    </li>
  );
}

function SubHeading({ children }: { children?: React.ReactNode }) {
  return (
    <h3 className="font-medium text-muted-foreground text-xs uppercase">
      {children}
    </h3>
  );
}

export function PayWallDialog({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <DialogContent size="4xl" className="overflow-hidden p-0 lg:h-[500px]">
        <div className="grid min-h-0 grid-cols-1 md:grid-cols-2">
          {/* Left Side - Simple Upgrade Button */}
          <div className="flex flex-col items-center justify-center p-6">
            <DialogHeader className="text-center">
              <DialogTitle>
                <Trans i18nKey="upgradePromptTitle" />
              </DialogTitle>
              <DialogDescription className="mt-2">
                <Trans
                  i18nKey="upgradePromptDescription"
                  defaults="Upgrade to Pro to unlock all features"
                />
              </DialogDescription>
            </DialogHeader>
            <div className="mt-8">
              <UpgradeButton className="w-full">
                <Trans i18nKey="upgrade" defaults="Upgrade" />
              </UpgradeButton>
            </div>
          </div>

          {/* Right Side - Pro Benefits */}
          <div className="overflow-y-auto bg-muted-background px-6 py-6">
            <div className="space-y-6">
              <DialogHeader>
                <DialogTitle>{PLAN_NAMES.PRO}</DialogTitle>
                <DialogDescription>
                  <Trans
                    i18nKey="planProDescription"
                    defaults="For professionals and power users"
                  />
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <SubHeading>
                  <Trans i18nKey="keyBenefits" defaults="Key Benefits" />
                </SubHeading>
                <KeyBenefits>
                  <KeyBenefitsItem
                    icon={<CalendarCheckIcon />}
                    title={
                      <Trans
                        i18nKey="featureNameFinalize"
                        defaults="Finalize Poll"
                      />
                    }
                    description={
                      <Trans
                        i18nKey="finalizePollDescription"
                        defaults="Lock in the final meeting time and notify participants"
                      />
                    }
                  />
                  <KeyBenefitsItem
                    icon={<CopyIcon />}
                    title={
                      <Trans
                        i18nKey="featureNameDuplicate"
                        defaults="Duplicate Poll"
                      />
                    }
                    description={
                      <Trans
                        i18nKey="duplicatePollDescription"
                        defaults="Quickly create similar polls from existing ones"
                      />
                    }
                  />
                  <KeyBenefitsItem
                    icon={<SettingsIcon />}
                    title={
                      <Trans
                        i18nKey="featureNameAdvancedSettings"
                        defaults="Advanced Settings"
                      />
                    }
                    description={
                      <Trans
                        i18nKey="advancedSettingsDescription"
                        defaults="Customize poll behavior and participant permissions"
                      />
                    }
                  />
                  <KeyBenefitsItem
                    icon={<ClockIcon />}
                    title={
                      <Trans
                        i18nKey="featureNameExtendedPollLifetime"
                        defaults="Extended Poll Lifetime"
                      />
                    }
                    description={
                      <Trans
                        i18nKey="extendedPollLifetimeDescription"
                        defaults="Keep polls indefinitely"
                      />
                    }
                  />
                  <KeyBenefitsItem
                    icon={<UserPlusIcon />}
                    title={
                      <Trans
                        i18nKey="teamCollaboration"
                        defaults="Team collaboration"
                      />
                    }
                    description={
                      <Trans
                        i18nKey="teamCollaborationDescription"
                        defaults="Invite team members with centralized billing"
                      />
                    }
                  />
                  <KeyBenefitsItem
                    icon={<LifeBuoyIcon />}
                    title={
                      <Trans
                        i18nKey="prioritySupport"
                        defaults="Priority support"
                      />
                    }
                    description={
                      <Trans
                        i18nKey="prioritySupportDescription"
                        defaults="Get faster response times and dedicated assistance"
                      />
                    }
                  />
                </KeyBenefits>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
